import {
  useState
} from 'react';
import {useTranslation} from 'react-i18next';
import {useApiTranslations} from '../../../../context/ApiTranslationsContext';
import {LicenseOrganizationController} from '../../../../api/license/license-organization.controller';
import {OrganizationController} from '../../../../api/organization/organization.controller';
import {LicenseOrganizationModel} from '../../../../model/license/license-organization.model';
import {OrganizationData} from '../../../../model/organization/organization-data';
import {OrganizationListPageHooks} from './organization-list-page-hooks';

export type OrganizationListPageState = {
  organizations: OrganizationData[]
  getOrganizationLicense: (organizationId: string) => LicenseOrganizationModel | null
  drawerOpen: boolean
  setDrawerOpen: (value: boolean) => void

  createOrganizationLicenseDrawerOpen: boolean
  setCreateOrganizationLicenseDrawerOpen: (value: boolean, organizationId: string | null) => void

  createOrganizationLicense: (licenseType: string, licenseExpiredDate: string, discount: number) => Promise<void>
  createOrganization: (name: string, publicKey: string) => Promise<void>
  toggleOrganizationActive: (organizationId: string, active: boolean) => Promise<void>

  translateColumn: (key: string) => string
  translate: (key: string) => string
}

export const useOrganizationListPageState = (): OrganizationListPageState => {
  const {t} = useTranslation()
  const {translate: apiTranslate} = useApiTranslations()
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOrganizationLicenseDrawerOpen, setCreateOrganizationLicenseDrawerOpen] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null)
  const [organizations, setOrganizations] = OrganizationListPageHooks.useOrganizations()
  const [organizationLicenses, _] = OrganizationListPageHooks.useOrganizationsLicense(organizations)

  const refreshOrganizations = async () => {
    setOrganizations(await OrganizationListPageHooks.fetchOrganizations())
  }

  const createOrganization = async (name: string, publicKey: string) => {
    await OrganizationController.createOrganization(name, publicKey)
    await refreshOrganizations()
  }

  const updateActive = async (organizationId: string, active: boolean) => {
    await OrganizationController.updateActive(organizationId, active)
    await refreshOrganizations()
  }

  const createOrganizationLicense = async (licenseType: string, licenseExpiredDate: string, discount: number)=> {
    if(selectedOrganizationId === null) {
      return;
    }

    const selectedOrganization = organizations.find(it => it.id === selectedOrganizationId) ?? null

    if(selectedOrganization === null) {
      return;
    }

    await LicenseOrganizationController.createOrganizationLicense(selectedOrganizationId, selectedOrganization.name, {
      licenseType: licenseType,
      licenseExpiredDate: licenseExpiredDate,
      discount: discount
    })

    await refreshOrganizations()
  }

  const getOrganizationLicense = (organizationId: string) =>
    organizationLicenses?.find(it => it.groupId === organizationId) ?? null

  return {
    translate: (key) => apiTranslate(key) || t(key),
    translateColumn: (key) => t(`organizations-page.column.${key}`),
    createOrganizationLicense: createOrganizationLicense,
    createOrganizationLicenseDrawerOpen: createOrganizationLicenseDrawerOpen,
    setCreateOrganizationLicenseDrawerOpen: (value, organizationId) => {
      setCreateOrganizationLicenseDrawerOpen(value)
      setSelectedOrganizationId(organizationId)
    },
    drawerOpen: drawerOpen,
    setDrawerOpen: setDrawerOpen,
    organizations: organizations,
    getOrganizationLicense: getOrganizationLicense,
    createOrganization: createOrganization,
    toggleOrganizationActive: updateActive
  }
}
